// src/components/Sidebar.tsx
import React, { useState, useMemo, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Collapse,
  ListItemIcon,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Storage from '@mui/icons-material/Storage';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Key from '@mui/icons-material/Key';
import VpnLock from '@mui/icons-material/VpnLock';
import Tag from '@mui/icons-material/Tag';
import Block from '@mui/icons-material/Block';
import Description from '@mui/icons-material/Description';
import Group from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import CommentIcon from '@mui/icons-material/Comment';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SendIcon from '@mui/icons-material/Send';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TagIcon from '@mui/icons-material/Tag';
import LockIcon from '@mui/icons-material/Lock';
import {Favorite, HelpOutline} from '@mui/icons-material';

interface LimitsResponse {
  user_level: string;
  is_staff: boolean;
  active_accounts: {
    current: number;
    limit: number;
    can_activate_more: boolean;
  };
  features: Record<string, boolean>;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [profileOpen, setProfileOpen] = useState(true);
  const [parsingOpen, setParsingOpen] = useState(true);
  const [neuroOpen, setNeuroOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [limits, setLimits] = useState<LimitsResponse | null>(null);

  // Загружаем лимиты один раз при монтировании
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const res = await api.get('/subscriptions/limits');
        setLimits(res.data);
      } catch (err) {
        console.error('Ошибка загрузки лимитов:', err);
      }
    };
    fetchLimits();
  }, []);

  // Маппинг пути → имя фичи в feature_limits
  const featureMap: Record<string, keyof LimitsResponse['features']> = {
    '/parsing/channel-message': 'parsing',
    '/parsing/channel-messages': 'parsing',
    '/parsing/small-group-members': 'parsing',
    '/parsing/large-group-members': 'parsing',
    '/parsing/find-channels': 'parsing',
    '/neuro_commenting': 'neurocommenting',
    '/neurocopywriting': 'neurocopywright',
    '/reactor': 'reactors',
    '/mass_sending': 'broadcast',
    '/channel_subscription': 'autosubscriptions',
    '/lead_generator': 'leadgen',
    '/inviter': 'inviter',
    '/story_mentions': 'story_mentions',
    '/warmup': 'warmup',
  };

  // Пути, недоступные для пользователей с уровнем free
  const freeBlockedPaths = [
    '/parsing/channel-messages',
    '/parsing/large-group-members',
  ];

  const isFeatureAvailable = (path: string): boolean => {
    if (!limits) return false; // пока не загрузились — блокируем

    // Блокируем для free-пользователей независимо от фичи parsing
    if (freeBlockedPaths.includes(path) && limits.user_level === 'free') {
      return false;
    }

    const feature = featureMap[path];
    if (!feature) return true; // если фича не ограничена — доступна

    const available = limits.features[feature] === true;
    return available;
  };

  const profileItems = [
    { text: 'Профиль', path: '/profile', icon: <AccountCircle /> },
      { text: 'Прокси', path: '/proxy', icon: <VpnLock /> },
    { text: 'Аккаунты', path: '/accounts', icon: <Storage /> },

    { text: 'Выгрузки', path: '/exports', icon: <CloudUpload /> },
    // { text: 'API', path: '/api', icon: <Key /> },
    { text: 'Ключевые слова', path: '/includewords', icon: <Tag /> },
    { text: 'Стоп-слова', path: '/stopwords', icon: <Block /> },
  ];

  const parsingItems = [
    { text: 'Сообщения (быстрый)', path: '/parsing/channel-message', icon: <Description /> },
    { text: 'Сообщения (полный)', path: '/parsing/channel-messages', icon: <Description /> },
    { text: 'Участники (до 10к)', path: '/parsing/small-group-members', icon: <Group /> },
    { text: 'Участники (10к+)', path: '/parsing/large-group-members', icon: <Group /> },
    { text: 'Поиск каналов', path: '/parsing/find-channels', icon: <SearchIcon /> },
  ];

  const neuroItems = [
    { text: 'Нейрокомментинг', path: '/neuro_commenting', icon: <CommentIcon /> },
    { text: 'Нейрокопирайтинг', path: '/neurocopywriting', icon: <AutorenewIcon /> },
    { text: 'Реакции', path: '/reactor', icon: <Favorite /> },
    { text: 'Рассылки', path: '/mass_sending', icon: <SendIcon /> },
    { text: 'Автоподписка на каналы', path: '/channel_subscription', icon: <GroupAddIcon /> },
    { text: 'Лидогенератор', path: '/lead_generator', icon: <SmartToyIcon /> },
    { text: 'Инвайтер', path: '/inviter', icon: <PersonAddIcon /> },
    { text: 'Отметки в сториз', path: '/story_mentions', icon: <TagIcon /> },
    { text: 'Прогрев аккаунтов', path: '/warmup', icon: <WhatshotIcon /> },
  ];

  const isActive = (path: string) => location.pathname === path;

  const filter = (items: any[]) => items.filter(i => i.text.toLowerCase().includes(searchQuery.toLowerCase()));

  const filtered = useMemo(() => ({
    profile: filter(profileItems),
    parsing: filter(parsingItems),
    neuro: filter(neuroItems),
   support: searchQuery.toLowerCase().includes('поддержка') || searchQuery === '' ? [{ text: 'Поддержка', path: '/support', icon: <HelpOutline /> }] : [],
}), [searchQuery]);

  const renderItem = (item: any) => {
    const available = isFeatureAvailable(item.path);

    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={isActive(item.path)}
          onClick={() => available && navigate(item.path)}
          disabled={!available}
          sx={{
            pl: 4,
            py: 1.2,
            borderRadius: 1,
            mb: 0.5,
            '&.Mui-selected': { bgcolor: available ? '#DBEAFE' : 'grey.100' },
            '&:hover': available ? { bgcolor: '#E0F2FE' } : { bgcolor: 'transparent' },
            opacity: available ? 1 : 0.6,
            cursor: available ? 'pointer' : 'not-allowed',
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: available ? '#1E40AF' : 'text.disabled' }}>
            {item.icon}
          </ListItemIcon>

          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: available ? 'text.primary' : 'text.disabled',
            }}
          />

          {!available && (
            <LockIcon fontSize="small" sx={{ color: 'text.disabled', ml: 1 }} />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderCollapsible = (title: string, items: any[], open: boolean, setOpen: (v: boolean) => void) => {
    const active = items.some(i => isActive(i.path));
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setOpen(!open)}
            sx={{
              py: 1.5,
              borderRadius: 1,
              mb: 0.5,
              bgcolor: open || active ? '#E0F2FE' : 'transparent',
              '&:hover': { bgcolor: '#DBEAFE' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {open ? <ExpandMore /> : <ChevronRight />}
            </ListItemIcon>
            <ListItemText primary={title} />
          </ListItemButton>
        </ListItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {items.map(renderItem)}
          </List>
        </Collapse>
      </>
    );
  };

  return (
    <Box
      sx={{
        width: 300,
        bgcolor: '#F9FAFB',
        borderRight: '1px solid #E5E7EB',
        p: 2,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2, fontWeight: 600 }}>
        Навигация
      </Typography>

      <TextField
        placeholder="Поиск..."
        size="small"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <List sx={{ p: 0 }}>
        {filtered.profile.length > 0 && renderCollapsible('Профиль', filtered.profile, profileOpen, setProfileOpen)}
        {filtered.parsing.length > 0 && renderCollapsible('Парсинг', filtered.parsing, parsingOpen, setParsingOpen)}
        {filtered.neuro.length > 0 && renderCollapsible('Автоматизации', filtered.neuro, neuroOpen, setNeuroOpen)}
          {filtered.support?.length > 0 && (
    <ListItem disablePadding>
      <ListItemButton
        selected={location.pathname === '/support'}
        onClick={() => navigate('/support')}
        sx={{
          py: 1.5,
          borderRadius: 1,
          mt: 1,
          bgcolor: location.pathname === '/support' ? '#E0F2FE' : 'transparent',
          '&:hover': { bgcolor: '#DBEAFE' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: '#1E40AF' }}>
          <HelpOutline />
        </ListItemIcon>
        <ListItemText
          primary="Поддержка"
          primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
        />
      </ListItemButton>
    </ListItem>
  )}
      </List>
    </Box>
  );
};

export default Sidebar;